import React, { useState, useRef, useEffect, useMemo } from 'react';
import { createPortal } from 'react-dom';
import { Download } from 'lucide-react';
import { OptimizationOpportunity } from '../../services/processOptimizationAPI';

// ── Types ─────────────────────────────────────────────────────────────────────

type Quadrant = 'quick_win' | 'strategic_bet' | 'fill_in' | 'reconsider';
type ViewScale = 'weeks' | 'months' | 'quarters';

interface GanttItem {
  id: number;
  title: string;
  startWeek: number;
  durationWeeks: number;
  endWeek: number;
  quadrant: Quadrant;
  isCriticalPath: boolean;
  prerequisiteIds: number[];
  opportunity: OptimizationOpportunity;
}

interface GanttRoadmapProps {
  opportunities: OptimizationOpportunity[];
  onOpportunityClick: (id: number) => void;
}

// ── Constants ─────────────────────────────────────────────────────────────────

const QUADRANT_COLORS: Record<Quadrant, string> = {
  quick_win: '#16a34a',
  strategic_bet: '#2563eb',
  fill_in: '#6b7280',
  reconsider: '#dc2626',
};

const QUADRANT_LIGHT: Record<Quadrant, string> = {
  quick_win: '#4ade80',
  strategic_bet: '#60a5fa',
  fill_in: '#d1d5db',
  reconsider: '#fca5a5',
};

const QUADRANT_LABELS: Record<Quadrant, string> = {
  quick_win: 'Quick Win',
  strategic_bet: 'Strategic Bet',
  fill_in: 'Fill-In',
  reconsider: 'Reconsider',
};

const LABEL_WIDTH = 248;
const ROW_HEIGHT = 52;
const BAR_HEIGHT = 28;
const BAR_MARGIN_TOP = (ROW_HEIGHT - BAR_HEIGHT) / 2;

// Phase background zones
const PHASE_ZONES = [
  { from: 0,  to: 12,       color: 'rgba(22,163,74,0.04)',   border: 'rgba(22,163,74,0.12)' },
  { from: 12, to: 26,       color: 'rgba(37,99,235,0.04)',   border: 'rgba(37,99,235,0.12)' },
  { from: 26, to: Infinity, color: 'rgba(109,40,217,0.04)',  border: 'rgba(109,40,217,0.12)' },
];

// ── Score helpers ─────────────────────────────────────────────────────────────

function normalizeToHundred(value: number, min: number, max: number): number {
  if (max === min) return 50;
  return Math.min(100, Math.max(0, ((value - min) / (max - min)) * 100));
}

function parseTimeReduction(s: string | undefined): number {
  if (!s) return 0;
  const m = s.match(/(\d+)/);
  return m ? Math.min(100, parseInt(m[1])) : 0;
}

function implComplexityToScore(c: string | undefined): number {
  if (c === 'low') return 25;
  if (c === 'high') return 75;
  return 50;
}

function getQuadrant(opp: OptimizationOpportunity): Quadrant {
  const complexity =
    0.30 * normalizeToHundred(opp.governance_complexity_score ?? 5, 1, 10) +
    0.25 * normalizeToHundred(opp.integration_complexity_score ?? 5, 1, 10) +
    0.20 * normalizeToHundred(opp.regulatory_risk_score ?? 5, 1, 10) +
    0.15 * implComplexityToScore(opp.implementation_complexity) +
    0.10 * normalizeToHundred(opp.dependencies?.prerequisites?.length ?? 0, 0, 5);

  const value =
    0.35 * normalizeToHundred(opp.automation_readiness_score ?? 5, 1, 10) +
    0.30 * parseTimeReduction(opp.estimated_time_reduction) +
    0.20 * normalizeToHundred(opp.affected_steps?.length ?? 1, 1, 20) +
    0.15 * normalizeToHundred(opp.workflow_count ?? 1, 1, 10);

  if (value >= 50 && complexity < 50) return 'quick_win';
  if (value >= 50 && complexity >= 50) return 'strategic_bet';
  if (value < 50 && complexity < 50) return 'fill_in';
  return 'reconsider';
}

function getDurationWeeks(opp: OptimizationOpportunity): number {
  const baseWeeks: Record<string, number> = { low: 4, medium: 8, high: 16 };
  const base = baseWeeks[opp.implementation_complexity ?? 'medium'] ?? 8;
  const phases = opp.phased_rollout_plan?.total_phases ?? 1;
  return base * Math.max(1, Math.min(phases, 3));
}

// ── Schedule Calculation ──────────────────────────────────────────────────────

interface ScheduleResult {
  items: GanttItem[];
  hasCycle: boolean;
  cycleIds: number[];
}

function calculateSchedule(opportunities: OptimizationOpportunity[]): ScheduleResult {
  if (!opportunities.length) return { items: [], hasCycle: false, cycleIds: [] };

  const prereqMap: Record<number, number[]> = {};
  const oppMap: Record<number, OptimizationOpportunity> = {};
  opportunities.forEach(opp => {
    oppMap[opp.id] = opp;
    prereqMap[opp.id] = (opp.dependencies?.prerequisites ?? [])
      .map(p => p.prerequisite_opportunity?.id ?? p.id)
      .filter((id): id is number => typeof id === 'number');
  });

  const ids = opportunities.map(o => o.id);
  const inDegree: Record<number, number> = {};
  const adj: Record<number, number[]> = {};
  ids.forEach(id => { inDegree[id] = 0; adj[id] = []; });
  ids.forEach(id => {
    prereqMap[id].forEach(preId => {
      if (adj[preId] !== undefined) {
        adj[preId].push(id);
        inDegree[id]++;
      }
    });
  });

  const queue = ids.filter(id => inDegree[id] === 0);
  const order: number[] = [];
  while (queue.length) {
    const curr = queue.shift()!;
    order.push(curr);
    adj[curr].forEach(next => { if (--inDegree[next] === 0) queue.push(next); });
  }

  const cycleIds = ids.filter(id => !order.includes(id));
  const hasCycle = cycleIds.length > 0;
  cycleIds.forEach(id => order.push(id));

  const es: Record<number, number> = {};
  const ef: Record<number, number> = {};
  order.forEach(id => {
    const preds = prereqMap[id] ?? [];
    es[id] = preds.length ? Math.max(...preds.map(pid => ef[pid] ?? 0)) : 0;
    ef[id] = es[id] + getDurationWeeks(oppMap[id]);
  });

  const maxEnd = Math.max(...Object.values(ef));
  const criticalSet = new Set<number>();
  const markCritical = (id: number, targetEnd: number) => {
    if (Math.abs(ef[id] - targetEnd) > 0.01) return;
    criticalSet.add(id);
    (prereqMap[id] ?? []).forEach(pid => markCritical(pid, es[id]));
  };
  order.forEach(id => { if (Math.abs(ef[id] - maxEnd) <= 0.01) markCritical(id, maxEnd); });

  return {
    hasCycle,
    cycleIds,
    items: order.map(id => ({
      id,
      title: oppMap[id].title,
      startWeek: es[id],
      durationWeeks: getDurationWeeks(oppMap[id]),
      endWeek: ef[id],
      quadrant: getQuadrant(oppMap[id]),
      isCriticalPath: criticalSet.has(id),
      prerequisiteIds: prereqMap[id] ?? [],
      opportunity: oppMap[id],
    })),
  };
}

// ── Timeline labels ───────────────────────────────────────────────────────────

function getTimelineLabels(totalWeeks: number, scale: ViewScale) {
  const labels: { label: string; week: number }[] = [];
  if (scale === 'weeks') {
    const step = totalWeeks <= 12 ? 1 : totalWeeks <= 24 ? 2 : 4;
    for (let w = 0; w <= totalWeeks; w += step) labels.push({ label: `W${w}`, week: w });
  } else if (scale === 'months') {
    const totalM = Math.ceil(totalWeeks / 4.33);
    for (let m = 0; m <= totalM; m++) labels.push({ label: `M${m + 1}`, week: Math.round(m * 4.33) });
  } else {
    const totalQ = Math.ceil(totalWeeks / 13);
    for (let q = 0; q <= totalQ; q++) labels.push({ label: `Q${q + 1}`, week: q * 13 });
  }
  return labels.filter(l => l.week <= totalWeeks + 1);
}

// ── Portal Tooltip ────────────────────────────────────────────────────────────

interface GanttBarTooltipProps {
  item: GanttItem;
  color: string;
  children: React.ReactNode;
}

const GanttBarTooltip: React.FC<GanttBarTooltipProps> = ({ item, color, children }) => {
  const [visible, setVisible] = useState(false);
  const [pos, setPos] = useState({ x: -9999, y: -9999 });
  const containerRef = useRef<HTMLDivElement>(null);
  const tooltipRef = useRef<HTMLDivElement>(null);

  useEffect(() => {
    if (visible && containerRef.current && tooltipRef.current) {
      const rect = containerRef.current.getBoundingClientRect();
      const tip = tooltipRef.current.getBoundingClientRect();
      const vw = window.innerWidth;

      let x = rect.left + rect.width / 2 - tip.width / 2;
      let y = rect.top - tip.height - 12;

      if (y < 8) y = rect.bottom + 8;
      if (x + tip.width > vw - 16) x = vw - tip.width - 16;
      if (x < 16) x = 16;

      setPos({ x, y });
    }
  }, [visible]);

  const complexityColor = {
    low: '#16a34a', medium: '#d97706', high: '#dc2626',
  }[item.opportunity.implementation_complexity ?? 'medium'] ?? '#d97706';

  const tooltip = visible ? (
    <div
      ref={tooltipRef}
      className="fixed z-[9999] pointer-events-none"
      style={{ left: pos.x, top: pos.y, minWidth: 240, maxWidth: 310 }}
    >
      {/* Arrow pointer */}
      <div className="flex justify-center mb-0.5">
        <div className="w-2 h-2 bg-gray-900 rotate-45 rounded-sm" />
      </div>
      <div className="bg-gray-900 text-white rounded-xl shadow-2xl overflow-hidden">
        {/* Color strip */}
        <div className="h-1 w-full" style={{ background: `linear-gradient(90deg, ${color}, ${QUADRANT_LIGHT[item.quadrant]})` }} />
        <div className="p-3">
          <p className="font-semibold text-xs mb-2 leading-snug line-clamp-2">{item.title}</p>
          {/* Timeline row */}
          <div className="flex items-center gap-1.5 mb-2">
            <div className="flex gap-0 rounded-md overflow-hidden border border-white/10 text-xs">
              <span className="bg-white/10 px-2 py-0.5 text-gray-300">W{item.startWeek}</span>
              <span className="text-gray-500 px-1 py-0.5">→</span>
              <span className="bg-white/10 px-2 py-0.5 text-gray-300">W{item.endWeek}</span>
            </div>
            <span className="ml-auto font-bold text-white text-xs">{item.durationWeeks}w</span>
          </div>
          {/* Tags row */}
          <div className="flex items-center gap-1.5 flex-wrap">
            <span
              className="px-1.5 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: color + '33', color }}
            >
              {QUADRANT_LABELS[item.quadrant]}
            </span>
            <span
              className="px-1.5 py-0.5 rounded text-xs font-medium"
              style={{ backgroundColor: complexityColor + '33', color: complexityColor }}
            >
              {item.opportunity.implementation_complexity ?? 'medium'} complexity
            </span>
            {item.isCriticalPath && (
              <span className="px-1.5 py-0.5 rounded text-xs font-medium bg-orange-500/20 text-orange-300">
                ⚡ Critical Path
              </span>
            )}
          </div>
          <p className="mt-2 text-indigo-400 font-medium text-xs">Click to view details →</p>
        </div>
      </div>
    </div>
  ) : null;

  return (
    <>
      <div
        ref={containerRef}
        onMouseEnter={() => { setPos({ x: -9999, y: -9999 }); setVisible(true); }}
        onMouseLeave={() => setVisible(false)}
        className="w-full h-full"
      >
        {children}
      </div>
      {tooltip && createPortal(tooltip, document.body)}
    </>
  );
};

// ── Main Component ────────────────────────────────────────────────────────────

const GanttRoadmap: React.FC<GanttRoadmapProps> = ({ opportunities, onOpportunityClick }) => {
  const containerRef = useRef<HTMLDivElement>(null);
  const trackRef = useRef<HTMLDivElement>(null);
  const [scale, setScale] = useState<ViewScale>('weeks');
  const [hoveredId, setHoveredId] = useState<number | null>(null);

  const schedule = useMemo(() => calculateSchedule(opportunities), [opportunities]);
  const { items, hasCycle, cycleIds } = schedule;
  const totalWeeks = useMemo(() => Math.max(...items.map(i => i.endWeek), 4), [items]);
  const labels = useMemo(() => getTimelineLabels(totalWeeks, scale), [totalWeeks, scale]);
  const criticalCount = items.filter(i => i.isCriticalPath).length;

  // Set of IDs related to hovered item (for arrow highlighting)
  const hoveredRelated = useMemo((): Set<number> => {
    if (hoveredId === null) return new Set();
    const s = new Set<number>();
    s.add(hoveredId);
    const item = items.find(i => i.id === hoveredId);
    if (item) {
      item.prerequisiteIds.forEach(pid => s.add(pid));
      items.forEach(i => { if (i.prerequisiteIds.includes(hoveredId)) s.add(i.id); });
    }
    return s;
  }, [hoveredId, items]);

  const milestones = [
    { week: 12, label: 'Phase Gate 1', short: 'PG1', color: '#16a34a' },
    { week: 26, label: 'Phase Gate 2', short: 'PG2', color: '#2563eb' },
  ].filter(m => m.week < totalWeeks);

  const pct = (week: number) => `${(week / totalWeeks) * 100}%`;

  const handleExport = async () => {
    const el = containerRef.current;
    if (!el) return;
    try {
      const html2canvas = (await import('html2canvas')).default;

      // Remove overflow clipping from container
      el.style.overflow = 'visible';

      // Unwrap all truncated text so full names show in PNG
      const truncated = Array.from(el.querySelectorAll<HTMLElement>('.truncate'));
      truncated.forEach(t => {
        t.style.overflow = 'visible';
        t.style.textOverflow = 'unset';
        t.style.whiteSpace = 'normal';
      });

      // Unwrap label column divs (fixed width clips text)
      const labelCols = Array.from(
        el.querySelectorAll<HTMLElement>('[data-export-label-col]')
      );
      labelCols.forEach(c => { c.style.overflow = 'visible'; });

      const canvas = await html2canvas(el, {
        backgroundColor: '#ffffff',
        scale: 2,
        useCORS: true,
        logging: false,
      });

      // Restore styles
      el.style.overflow = '';
      truncated.forEach(t => {
        t.style.overflow = '';
        t.style.textOverflow = '';
        t.style.whiteSpace = '';
      });
      labelCols.forEach(c => { c.style.overflow = ''; });

      const link = document.createElement('a');
      link.download = 'implementation-roadmap.png';
      link.href = canvas.toDataURL('image/png');
      link.click();
    } catch (e) {
      if (containerRef.current) containerRef.current.style.overflow = '';
      console.error('Export failed:', e);
    }
  };

  if (!items.length) {
    return (
      <div className="text-center py-16 text-gray-400 bg-white rounded-xl border border-gray-200">
        <div className="text-4xl mb-3">📋</div>
        <p className="font-medium">No opportunities to display</p>
      </div>
    );
  }

  const totalHeight = items.length * ROW_HEIGHT;

  return (
    <div className="space-y-4">

      {/* ── Circular Dependency Warning ─────────────────────────────────── */}
      {hasCycle && (
        <div className="flex items-start gap-3 bg-amber-50 border border-amber-300 rounded-xl px-4 py-3">
          <span className="text-amber-500 text-lg shrink-0">⚠️</span>
          <div>
            <p className="text-sm font-bold text-amber-800">Circular Dependency Detected</p>
            <p className="text-xs text-amber-700 mt-0.5">
              {cycleIds.length} opportunit{cycleIds.length === 1 ? 'y has' : 'ies have'} circular
              dependencies and {cycleIds.length === 1 ? 'has' : 'have'} been placed at the end.
              Review prerequisite settings for accurate scheduling.
            </p>
          </div>
        </div>
      )}

      {/* ── Header Card ─────────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

        <div className="p-4">
          <div className="flex items-center justify-between flex-wrap gap-3">
            <div>
              <h2 className="text-lg font-bold text-gray-900">Implementation Roadmap</h2>
              <p className="text-xs text-gray-500 mt-0.5">Sequenced by dependencies · Critical path highlighted</p>
            </div>

            {/* Stats chips */}
            <div className="flex items-center gap-2 flex-wrap">
              <div className="flex items-center gap-1.5 bg-gray-100 px-3 py-1.5 rounded-full">
                <span className="text-xs font-bold text-gray-700">{totalWeeks}w</span>
                <span className="text-xs text-gray-500">total</span>
              </div>
              <div className="flex items-center gap-1.5 bg-orange-50 border border-orange-200 px-3 py-1.5 rounded-full">
                <span className="w-1.5 h-1.5 rounded-full bg-orange-400 shrink-0" />
                <span className="text-xs font-bold text-orange-700">{criticalCount}</span>
                <span className="text-xs text-orange-600">critical path</span>
              </div>
              <div className="flex items-center gap-1.5 bg-indigo-50 border border-indigo-200 px-3 py-1.5 rounded-full">
                <span className="text-xs font-bold text-indigo-700">{items.length}</span>
                <span className="text-xs text-indigo-600">opportunities</span>
              </div>
            </div>

            {/* Controls */}
            <div className="flex items-center gap-2">
              <div className="flex rounded-lg border border-gray-200 overflow-hidden text-xs font-medium bg-gray-50">
                {(['weeks', 'months', 'quarters'] as ViewScale[]).map(s => (
                  <button
                    key={s}
                    onClick={() => setScale(s)}
                    className={`px-3 py-1.5 capitalize transition-all ${
                      scale === s
                        ? 'bg-indigo-600 text-white shadow-sm'
                        : 'text-gray-600 hover:bg-white hover:text-gray-800'
                    }`}
                  >
                    {s}
                  </button>
                ))}
              </div>
              <button
                onClick={handleExport}
                className="flex items-center gap-1.5 px-3 py-1.5 text-xs font-medium text-gray-600 bg-white border border-gray-300 rounded-lg hover:bg-gray-50 hover:text-gray-900 transition-colors"
              >
                <Download className="w-3.5 h-3.5" />
                Export PNG
              </button>
            </div>
          </div>

          {/* Legend */}
          <div className="flex items-center gap-4 mt-3 pt-3 border-t border-gray-100 flex-wrap">
            {(Object.entries(QUADRANT_COLORS) as [Quadrant, string][]).map(([q, color]) => (
              <div key={q} className="flex items-center gap-1.5">
                <span className="w-3 h-2.5 rounded-sm inline-block" style={{ background: `linear-gradient(90deg, ${QUADRANT_LIGHT[q]}, ${color})` }} />
                <span className="text-xs text-gray-500">{QUADRANT_LABELS[q]}</span>
              </div>
            ))}
            <div className="flex items-center gap-1.5">
              <span className="w-3 h-2.5 rounded-sm inline-block border-2 border-orange-400 bg-orange-50" />
              <span className="text-xs text-gray-500">Critical Path</span>
            </div>
            <div className="flex items-center gap-1.5 ml-auto">
              <span className="text-xs text-gray-400 italic">Hover bar for details · Click to open</span>
            </div>
          </div>
        </div>
      </div>

      {/* ── Gantt Chart ─────────────────────────────────────────────────── */}
      <div ref={containerRef} className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">

        {/* Timeline header */}
        <div className="flex border-b border-gray-200" style={{ height: 40, background: 'linear-gradient(to bottom, #f9fafb, #f3f4f6)' }}>
          <div
            className="shrink-0 flex items-center px-4 border-r border-gray-200"
            style={{ width: LABEL_WIDTH }}
          >
            <span className="text-[10px] font-bold text-gray-400 tracking-widest uppercase">Opportunity</span>
          </div>
          <div className="flex-1 relative overflow-hidden" style={{ height: 40 }}>
            {/* Phase zone header labels */}
            {milestones.length > 0 && (
              <>
                <span
                  className="absolute text-[9px] font-bold text-green-600/50 uppercase tracking-wider"
                  style={{ left: '1%', top: 6 }}
                >
                  Quick Wins
                </span>
                {milestones[0] && milestones[1] && (
                  <span
                    className="absolute text-[9px] font-bold text-blue-600/50 uppercase tracking-wider"
                    style={{ left: `calc(${(12 / totalWeeks) * 100}% + 4px)`, top: 6 }}
                  >
                    Foundation
                  </span>
                )}
                {milestones[1] && (
                  <span
                    className="absolute text-[9px] font-bold text-purple-600/50 uppercase tracking-wider"
                    style={{ left: `calc(${(26 / totalWeeks) * 100}% + 4px)`, top: 6 }}
                  >
                    Transformation
                  </span>
                )}
              </>
            )}
            {/* Week/month/quarter labels */}
            {labels.map(({ label, week }) => (
              <React.Fragment key={label}>
                <span
                  className="absolute text-[10px] text-gray-400 font-medium"
                  style={{ left: pct(week), bottom: 6, transform: 'translateX(-50%)' }}
                >
                  {label}
                </span>
                <div
                  className="absolute top-0 bottom-0 border-l border-gray-200/80"
                  style={{ left: pct(week) }}
                />
              </React.Fragment>
            ))}
          </div>
        </div>

        {/* Rows + SVG arrows overlay */}
        <div className="relative" style={{ minHeight: totalHeight }}>

          {/* ── Phase background bands ─────────────────────────────────── */}
          <div className="absolute inset-0 pointer-events-none" style={{ left: LABEL_WIDTH }}>
            {PHASE_ZONES.map((zone, zi) => {
              const fromPct = (Math.min(zone.from, totalWeeks) / totalWeeks) * 100;
              const toPct = (Math.min(zone.to, totalWeeks) / totalWeeks) * 100;
              if (fromPct >= 100) return null;
              return (
                <div
                  key={zi}
                  className="absolute top-0 bottom-0"
                  style={{
                    left: `${fromPct}%`,
                    width: `${toPct - fromPct}%`,
                    backgroundColor: zone.color,
                    borderRight: `1px dashed ${zone.border}`,
                  }}
                />
              );
            })}
          </div>

          {/* ── SVG dependency arrows ──────────────────────────────────── */}
          <svg
            className="absolute pointer-events-none"
            style={{
              left: LABEL_WIDTH,
              top: 0,
              width: `calc(100% - ${LABEL_WIDTH}px)`,
              height: totalHeight,
              zIndex: 5,
              overflow: 'visible',
            }}
          >
            <defs>
              <marker id="arrow-normal" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L0,6 L6,3 z" fill="#94a3b8" />
              </marker>
              <marker id="arrow-active" markerWidth="6" markerHeight="6" refX="5" refY="3" orient="auto">
                <path d="M0,0 L0,6 L6,3 z" fill="#6366f1" />
              </marker>
            </defs>
            {items.map((item, rowIdx) =>
              item.prerequisiteIds.map(preId => {
                const preIdx = items.findIndex(i => i.id === preId);
                if (preIdx === -1) return null;
                const pre = items[preIdx];
                const trackW = trackRef.current?.clientWidth ?? 800;
                const x1 = (pre.endWeek / totalWeeks) * trackW;
                const y1 = preIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
                const x2 = (item.startWeek / totalWeeks) * trackW;
                const y2 = rowIdx * ROW_HEIGHT + ROW_HEIGHT / 2;
                const cx = (x1 + x2) / 2;

                const isActive = hoveredRelated.size > 0 &&
                  hoveredRelated.has(item.id) && hoveredRelated.has(preId);
                const isConnected = hoveredRelated.has(item.id) || hoveredRelated.has(preId);
                const opacity = hoveredRelated.size === 0 ? 0.2 : isConnected ? (isActive ? 1 : 0.15) : 0.08;
                const stroke = isActive ? '#6366f1' : '#94a3b8';
                const strokeW = isActive ? 2 : 1.5;

                return (
                  <path
                    key={`${preId}->${item.id}`}
                    d={`M${x1},${y1} C${cx},${y1} ${cx},${y2} ${x2},${y2}`}
                    fill="none"
                    stroke={stroke}
                    strokeWidth={strokeW}
                    strokeDasharray={isActive ? '0' : '4 3'}
                    opacity={opacity}
                    markerEnd={isActive ? 'url(#arrow-active)' : 'url(#arrow-normal)'}
                    style={{ transition: 'opacity 0.15s, stroke 0.15s' }}
                  />
                );
              })
            )}
          </svg>

          {/* ── Today line ────────────────────────────────────────────── */}
          <div
            className="absolute top-0 bottom-0 z-10 pointer-events-none"
            style={{ left: LABEL_WIDTH, width: 2, background: 'linear-gradient(to bottom, #ef4444, #fca5a5)', opacity: 0.9 }}
          >
            <span
              className="absolute -left-0.5 top-2 text-[9px] font-bold text-red-500 whitespace-nowrap bg-red-50 border border-red-200 rounded px-1 py-0.5"
            >
              Today
            </span>
          </div>

          {/* ── Phase gate milestone lines ─────────────────────────────── */}
          {milestones.map(m => (
            <div
              key={`milestone-${m.week}`}
              className="absolute top-0 bottom-0 z-10 pointer-events-none"
              style={{
                left: `calc(${LABEL_WIDTH}px + (100% - ${LABEL_WIDTH}px) * ${m.week / totalWeeks})`,
                width: 2,
                background: `linear-gradient(to bottom, ${m.color}, ${m.color}44)`,
                opacity: 0.6,
              }}
            >
              {/* Diamond marker at top */}
              <div
                className="absolute w-3 h-3 rotate-45 -top-1.5"
                style={{
                  left: -5,
                  backgroundColor: m.color,
                  boxShadow: `0 0 6px ${m.color}80`,
                }}
              />
              {/* Label at bottom */}
              <span
                className="absolute whitespace-nowrap font-bold rounded-full px-2 py-0.5 text-white text-[9px]"
                style={{
                  bottom: 8,
                  left: 5,
                  backgroundColor: m.color,
                  boxShadow: `0 2px 6px ${m.color}50`,
                }}
              >
                {m.short}
              </span>
            </div>
          ))}

          {/* ── Rows ──────────────────────────────────────────────────── */}
          {items.map((item, idx) => {
            const isHovered = hoveredId === item.id;
            const color = QUADRANT_COLORS[item.quadrant];
            const leftPct = (item.startWeek / totalWeeks) * 100;
            const widthPct = Math.max((item.durationWeeks / totalWeeks) * 100, 1.2);
            const complexity = item.opportunity.implementation_complexity ?? 'medium';
            const complexityDot: Record<string, string> = { low: '#16a34a', medium: '#d97706', high: '#dc2626' };
            const isRelated = hoveredRelated.has(item.id) && !isHovered;

            return (
              <div
                key={item.id}
                className="flex border-b border-gray-100 transition-colors duration-75"
                style={{
                  height: ROW_HEIGHT,
                  backgroundColor: isHovered
                    ? `${color}08`
                    : isRelated
                    ? '#eef2ff'
                    : idx % 2 === 0 ? 'transparent' : 'rgba(249,250,251,0.5)',
                  borderLeft: item.isCriticalPath ? `3px solid ${color}` : '3px solid transparent',
                }}
                onMouseEnter={() => setHoveredId(item.id)}
                onMouseLeave={() => setHoveredId(null)}
              >
                {/* ── Label column ──────────────────────────────────── */}
                <div
                  className="shrink-0 flex items-center gap-2 px-3 border-r border-gray-200 cursor-pointer group"
                  data-export-label-col=""
                  style={{ width: LABEL_WIDTH - 3 /* account for left border */ }}
                  onClick={() => onOpportunityClick(item.id)}
                  title={item.title}
                >
                  {/* Row number */}
                  <span className="shrink-0 text-[10px] font-bold text-gray-300 w-4 text-right">
                    {idx + 1}
                  </span>
                  {/* Complexity dot */}
                  <span
                    className="shrink-0 w-1.5 h-1.5 rounded-full"
                    style={{ backgroundColor: complexityDot[complexity] }}
                    title={`${complexity} complexity`}
                  />
                  {/* Title */}
                  <span className={`flex-1 text-xs font-medium truncate leading-tight transition-colors ${isHovered ? 'text-gray-900' : 'text-gray-600'} group-hover:text-gray-900`}>
                    {item.title}
                  </span>
                  {/* Critical path badge */}
                  {item.isCriticalPath && (
                    <span className="shrink-0 text-[9px] font-bold text-orange-500 bg-orange-50 border border-orange-200 rounded-full px-1.5 py-0.5 leading-none">
                      CP
                    </span>
                  )}
                </div>

                {/* ── Track ─────────────────────────────────────────── */}
                <div
                  ref={idx === 0 ? trackRef : undefined}
                  className="flex-1 relative"
                  style={{ height: ROW_HEIGHT }}
                >
                  {/* Vertical grid lines */}
                  {labels.map(({ label, week }) => (
                    <div
                      key={`g-${item.id}-${label}`}
                      className="absolute top-0 bottom-0 border-l border-gray-100/80"
                      style={{ left: pct(week) }}
                    />
                  ))}

                  {/* Gantt bar */}
                  <div
                    className="absolute cursor-pointer"
                    style={{
                      left: `${leftPct}%`,
                      width: `${widthPct}%`,
                      top: BAR_MARGIN_TOP,
                      height: BAR_HEIGHT,
                    }}
                    onClick={() => onOpportunityClick(item.id)}
                  >
                    <GanttBarTooltip item={item} color={color}>
                      <div
                        className="w-full h-full flex items-center px-2.5 overflow-hidden transition-all duration-150"
                        style={{
                          backgroundColor: isHovered ? color : color + 'dd',
                          borderRadius: 6,
                          border: item.isCriticalPath
                            ? `2px solid #f97316`
                            : `1px solid ${color}55`,
                          boxShadow: isHovered ? `0 2px 8px ${color}40` : 'none',
                        }}
                      >
                        <span className="text-white text-[10px] font-semibold truncate">
                          {widthPct > 6 ? `${item.durationWeeks}w` : ''}
                        </span>
                      </div>
                    </GanttBarTooltip>
                  </div>
                </div>
              </div>
            );
          })}
        </div>
      </div>

      {/* ── Phase Summary ───────────────────────────────────────────────── */}
      <div className="bg-white border border-gray-200 rounded-xl shadow-sm overflow-hidden">
        <div className="px-5 py-3 border-b border-gray-100 flex items-center justify-between">
          <h3 className="text-sm font-bold text-gray-800">Phase Summary</h3>
          <span className="text-xs text-gray-400">{totalWeeks} weeks total program</span>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3">
          {[
            {
              name: 'Phase 1',
              sub: 'Quick Wins',
              range: '0 – 12 weeks',
              color: '#16a34a',
              lightColor: '#4ade80',
              bg: 'linear-gradient(135deg, #f0fdf4 0%, #dcfce7 100%)',
              border: '#bbf7d0',
              progressPct: Math.min(100, (12 / totalWeeks) * 100),
              items: items.filter(i => i.quadrant === 'quick_win'),
            },
            {
              name: 'Phase 2',
              sub: 'Foundation',
              range: '12 – 26 weeks',
              color: '#2563eb',
              lightColor: '#60a5fa',
              bg: 'linear-gradient(135deg, #eff6ff 0%, #dbeafe 100%)',
              border: '#bfdbfe',
              progressPct: Math.min(100, Math.max(0, ((Math.min(26, totalWeeks) - 12) / totalWeeks) * 100)),
              items: items.filter(i => i.quadrant !== 'quick_win' && i.startWeek < 26),
            },
            {
              name: 'Phase 3',
              sub: 'Transformation',
              range: '26+ weeks',
              color: '#7c3aed',
              lightColor: '#a78bfa',
              bg: 'linear-gradient(135deg, #faf5ff 0%, #ede9fe 100%)',
              border: '#ddd6fe',
              progressPct: Math.min(100, Math.max(0, ((totalWeeks - 26) / totalWeeks) * 100)),
              items: items.filter(i => i.quadrant === 'strategic_bet' || i.startWeek >= 26),
            },
          ].map((phase, pi) => (
            <div
              key={phase.name}
              className="p-5 relative overflow-hidden"
              style={{
                background: phase.bg,
                borderRight: pi < 2 ? `1px solid ${phase.border}` : 'none',
              }}
            >
              {/* Background number watermark */}
              <span
                className="absolute right-3 top-2 text-6xl font-black opacity-[0.04] select-none"
                style={{ color: phase.color }}
              >
                {pi + 1}
              </span>

              <div className="flex items-start justify-between mb-2">
                <div>
                  <div className="flex items-center gap-1.5 mb-0.5">
                    <span className="w-2 h-2 rounded-full shrink-0" style={{ backgroundColor: phase.color }} />
                    <p className="text-[10px] font-bold uppercase tracking-wider" style={{ color: phase.color }}>
                      {phase.name}
                    </p>
                  </div>
                  <p className="text-sm font-bold text-gray-900">{phase.sub}</p>
                  <p className="text-xs text-gray-500">{phase.range}</p>
                </div>
                <div className="text-right">
                  <p className="text-3xl font-black text-gray-900 leading-none">{phase.items.length}</p>
                  <p className="text-[10px] text-gray-500 mt-0.5">items</p>
                </div>
              </div>

              {/* Progress bar */}
              <div className="h-1.5 bg-white/60 rounded-full overflow-hidden mb-3">
                <div
                  className="h-full rounded-full transition-all"
                  style={{
                    width: `${phase.progressPct}%`,
                    background: `linear-gradient(90deg, ${phase.lightColor}, ${phase.color})`,
                  }}
                />
              </div>

              {/* Item list */}
              {phase.items.length > 0 ? (
                <ul className="space-y-1">
                  {phase.items.slice(0, 3).map(i => (
                    <li key={i.id} className="flex items-center gap-1.5 text-xs text-gray-600">
                      <span className="w-1 h-1 rounded-full shrink-0" style={{ backgroundColor: QUADRANT_COLORS[i.quadrant] }} />
                      <span className="truncate">{i.title}</span>
                    </li>
                  ))}
                  {phase.items.length > 3 && (
                    <li className="text-xs text-gray-400 pl-2.5">+{phase.items.length - 3} more</li>
                  )}
                </ul>
              ) : (
                <p className="text-xs text-gray-400 italic">No items in this phase</p>
              )}
            </div>
          ))}
        </div>
      </div>

    </div>
  );
};

export default GanttRoadmap;
